import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import RunnerAvatar from './RunnerAvatar';
import { playRaceFanfare } from 'common/utils/sfx';
import { useBackDismiss } from 'hooks/useBackDismiss';
import {
    IcFinishFlag, IcRun, IcTrophy, IcClose, IcCrown, IcMedal, IcCafe,
    IcConstruction, IcCheckCircle, IcGift, IcBeer, IcRestaurant, IcSlot, IcTurtle,
} from 'components/common/icons/EmojiIcons';
import styles from './RaceFinishModal.module.css';

/** 클래스 이름을 해시된 이름으로 바꿉니다. 모르는 이름은 그대로 둡니다.
 *  이름을 바꾸지 않고 스코프만 씌우려고 둔 얇은 헬퍼입니다. */
const cls = (names?: string): string =>
    (names || '').split(' ').filter(Boolean)
        .map((n) => (styles as Record<string, string>)[n] || n).join(' ');

export interface RaceFinishRow {
    userNo: number;
    nickname: string;
    rank: number;        // 1부터
    arrived: boolean;
    lateMinutes: number; // 지각 분 (≥0)
    isLast: boolean;
    excuseReason?: string | null;
    excuseApproved?: boolean;
    profileImgUrl?: string;
}

export interface RaceFinishModalProps {
    open: boolean;
    rows: RaceFinishRow[];
    currentUserNo: number | null;
    lateFeeAmount: number;
    shuttlePenaltyEnabled: boolean;
    isHost?: boolean;
    onApproveExcuse?: (targetUserNo: number, approved: boolean) => void;
    onClose: () => void;
}

const RaceFinishModal: React.FC<RaceFinishModalProps> = ({
    open,
    rows,
    currentUserNo,
    lateFeeAmount,
    shuttlePenaltyEnabled,
    isHost = false,
    onApproveExcuse,
    onClose,
}) => {
    const { t } = useTranslation();
    const [step, setStep] = useState<'intro' | 'result'>('intro');

    useEffect(() => {
        if (!open) {
            setStep('intro');
            return;
        }
        const t = setTimeout(() => { setStep('result'); playRaceFanfare(); }, 1800);
        return () => clearTimeout(t);
    }, [open]);

    const myRow = useMemo(
        () => rows.find(r => r.userNo === currentUserNo),
        [rows, currentUserNo]
    );

    useBackDismiss(open, onClose);
    if (!open) return null;

    return (
        <div className={cls("race-finish-overlay")} onClick={onClose}>
            <div className={cls("race-finish-modal")} onClick={e => e.stopPropagation()}>
                {step === 'intro' ? (
                    <div className={cls("race-finish-intro")}>
                        <div className={cls("race-finish-banner")}><IcFinishFlag size={18} /> RACE FINISH <IcFinishFlag size={18} /></div>
                        <div className={cls("race-finish-runners")}>
                            <span className={cls("rf-run rf-run-1")}><IcRun size={20} /></span>
                            <span className={cls("rf-run rf-run-2")}><IcRun size={20} /></span>
                            <span className={cls("rf-run rf-run-3")}><IcRun size={20} /></span>
                        </div>
                        <div className={cls("race-finish-subtitle")}>{t('race.aggregating')}</div>
                    </div>
                ) : (
                    <>
                        <header className={cls("race-finish-header")}>
                            <h2><IcTrophy size={20} /> {t('race.finalRank')}</h2>
                            <button className={cls("race-finish-close")} onClick={onClose}><IcClose size={16} /></button>
                        </header>

                        {myRow && (
                            <div className={cls(`race-finish-mine rank-${Math.min(myRow.rank, 4)}`)}>
                                {myRow.rank === 1 && <><IcCrown size={16} /> {t('race.youWin')}</>}
                                {myRow.rank === 2 && <><IcMedal size={16} /> 2위 입상!</>}
                                {myRow.rank === 3 && <><IcMedal size={16} /> 3위 입상!</>}
                                {myRow.rank > 3 && !myRow.isLast && <>{myRow.rank}위로 도착 <IcRun size={16} /></>}
                                {myRow.isLast && <><IcTurtle size={16} /> {t('race.shuttleWin')}</>}
                            </div>
                        )}

                        <ul className={cls("race-finish-list")}>
                            {rows.map(r => (
                                <li
                                    key={r.userNo}
                                    className={cls(`rf-item rank-${Math.min(r.rank, 4)} ${r.isLast ? 'is-last' : ''}`)}
                                >
                                    <div className={cls("rf-rank")}>
                                        {r.rank <= 3 ? <IcMedal size={18} /> : r.rank}
                                    </div>
                                    <RunnerAvatar
                                        state={r.arrived ? 'arrived' : r.lateMinutes >= 10 ? 'panic' : 'rush'}
                                        rank={r.rank <= 3 ? r.rank : undefined}
                                        isLast={r.isLast}
                                        nickname={r.nickname}
                                        profileImgUrl={r.profileImgUrl}
                                        size={44}
                                    />
                                    <div className={cls("rf-info")}>
                                        <div className={cls("rf-name")}>{r.nickname}</div>
                                        <div className={cls("rf-status")}>
                                            {r.arrived
                                                ? t('race.onTimeArrive')
                                                : r.lateMinutes > 0
                                                    ? t('race.lateMin', { n: r.lateMinutes })
                                                    : t('race.soonArrive')}
                                        </div>
                                    </div>
                                    {r.lateMinutes > 0 && lateFeeAmount > 0 && (
                                        <div className={cls("rf-fine")}>
                                            {t('race.finePlus', {
                                                amount: t('meetingFinished.amountWon', { amount: lateFeeAmount.toLocaleString('ko-KR') }),
                                            })}
                                        </div>
                                    )}
                                    {r.isLast && shuttlePenaltyEnabled && (
                                        <div className={cls("rf-shuttle")}><IcCafe size={12} /> {t('race.shuttle')}</div>
                                    )}
                                </li>
                            ))}
                        </ul>

                        {/* 주최자 — 사유 승인/거절 */}
                        {isHost && rows.some(r => r.excuseReason) && (
                            <div className={cls("race-finish-excuses")}>
                                <strong><IcConstruction size={14} /> {t('race.excuseApproval')}</strong>
                                {rows.filter(r => r.excuseReason).map(r => (
                                    <div key={r.userNo} className={cls("rf-excuse-row")}>
                                        <div className={cls("rf-excuse-info")}>
                                            <div className={cls("rf-excuse-name")}>{r.nickname}</div>
                                            <div className={cls("rf-excuse-reason")}>"{r.excuseReason}"</div>
                                        </div>
                                        {r.excuseApproved ? (
                                            <span className={cls("rf-excuse-approved")}><IcCheckCircle size={13} /> {t('race.approved')}</span>
                                        ) : onApproveExcuse ? (
                                            <div className={cls("rf-excuse-buttons")}>
                                                <button className={cls("rf-excuse-approve")} onClick={() => onApproveExcuse(r.userNo, true)}>
                                                    승인
                                                </button>
                                                <button className={cls("rf-excuse-reject")} onClick={() => onApproveExcuse(r.userNo, false)}>
                                                    거절
                                                </button>
                                            </div>
                                        ) : null}
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className={cls("race-finish-rewards")}>
                            <strong><IcGift size={14} /> {t('race.winnerPerks')}</strong>
                            <ul>
                                <li><IcBeer size={14} /> {t('race.perk1')}</li>
                                <li><IcRestaurant size={14} /> {t('race.perk2')}</li>
                                <li><IcSlot size={14} /> {t('race.perk3')}</li>
                            </ul>
                        </div>

                        <button className={cls("race-finish-ok")} onClick={onClose}>
                            {t('race.okBtn')}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default RaceFinishModal;
